package dev.kuml.portal.web.editor

/**
 * Pure project-name -> download-filename derivation for R-7's SVG/PNG downloads (see
 * `DiagramFileNameTest`). The name is user-controlled free text (the editor's inline rename), so it
 * is sanitised before it ever reaches an `<a download>` attribute: path separators and the Windows-
 * reserved set are the interesting cases -- a `download` value containing `/` or `..` is ignored or
 * flattened by browsers rather than obeyed, but sanitising here means the user gets the filename
 * they expect instead of a browser-chosen fallback.
 *
 * Non-ASCII is deliberately PRESERVED (umlauts, Cyrillic, Georgian): the `download` attribute is a
 * UTF-8 string and every evergreen browser writes such names correctly; transliterating them would
 * only make German project names worse.
 */
public object DiagramFileName {
    /** Used when sanitisation leaves nothing usable (blank name, name consisting only of illegal
     * characters), and only when the caller does not supply its own [forDiagramName] `fallbackBase`
     * -- German, matching this constant's pre-R-18 default. Prefer passing the active language's
     * `t.editor.diagramFileFallback` instead (see [forDiagramName]'s `fallbackBase` parameter). */
    public const val FALLBACK_BASE: String = "kuml-diagramm"

    private const val MAX_BASE_LENGTH: Int = 60

    /** Path separators, the Windows-reserved set, and C0 controls. */
    private val ILLEGAL = Regex("[\\\\/:*?\"<>|\\x00-\\x1F]")

    /** Any run of whitespace/underscore/dash collapses to a single dash. */
    private val COLLAPSE = Regex("[\\s_-]+")

    /** Leading/trailing dots and dashes -- a leading dot would produce a hidden file on Unix. */
    private val TRIM_EDGES = Regex("^[.\\-]+|[.\\-]+$")

    /**
     * @param diagramName the editor's current diagram tab name (R-22 -- was the project name
     *   before a project could hold several diagrams; may be the server-assigned default, may be
     *   blank).
     * @param extension without a leading dot, e.g. `"svg"` or `"png"`.
     * @param fallbackBase R-18: the active language's fallback base name (`t.editor.diagramFileFallback`,
     *   `"kuml-diagramm"`/`"kuml-diagram"`), used when sanitisation leaves nothing usable. Defaults
     *   to [FALLBACK_BASE] for existing callers/tests that don't care about localisation.
     */
    public fun forDiagramName(
        diagramName: String,
        extension: String,
        fallbackBase: String = FALLBACK_BASE,
    ): String {
        val collapsed = TRIM_EDGES.replace(COLLAPSE.replace(ILLEGAL.replace(diagramName, "-"), "-"), "")
        // Trimmed twice: truncating at MAX_BASE_LENGTH can itself leave a trailing dash/dot behind.
        val base = TRIM_EDGES.replace(collapsed.take(MAX_BASE_LENGTH), "").ifBlank { fallbackBase }
        return "$base.$extension"
    }
}
